"use client";

import { useState, useEffect } from "react";
import { Select, SelectItem, Button, addToast, Image } from "@heroui/react";
import { gameAssetUrl } from "@/lib/cdn";

interface ArchetypeSelectorProps {
  decklistId: string;
  currentArchetypeId?: string | null;
  gameId: number;
}

interface Archetype {
  name: string;
  display_name: string;
  colors: string[];
}

export default function ArchetypeSelector({
  decklistId,
  currentArchetypeId,
  gameId,
}: ArchetypeSelectorProps) {
  const [archetypes, setArchetypes] = useState<Archetype[]>([]);
  const [selectedArchetype, setSelectedArchetype] = useState<string>(
    currentArchetypeId || "",
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Only show in development or if explicitly enabled
  const isDev = process.env.NODE_ENV === "development";
  const isEnabled =
    isDev || process.env.NEXT_PUBLIC_ENABLE_ARCHETYPE_SELECTOR === "true";

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const fetchArchetypes = async () => {
      setLoading(true);
      try {
        // Try fetching from Next.js API route first (which can use server-side env vars)
        const internalUrl = `/api/archetypes-list?game_id=${gameId}`;
        const internalResponse = await fetch(internalUrl);

        if (internalResponse.ok) {
          const data = await internalResponse.json();
          setArchetypes(data || []);
          return;
        }

        // Fallback to direct backend call
        const apiUrl =
          process.env.NEXT_PUBLIC_API_URL ||
          (process.env.NODE_ENV === "development"
            ? "http://localhost:3001"
            : "https://api.tcgfan.com");
        const secretKey = process.env.NEXT_PUBLIC_BACKEND_SECRET_KEY;

        if (!secretKey) {
          console.error("NEXT_PUBLIC_BACKEND_SECRET_KEY not configured!");
          addToast({
            title: "Error",
            description: "Backend secret key not configured",
            color: "danger",
          });
          return;
        }

        const url = `${apiUrl}/api/archetypes?game_id=${gameId}&API_KEY=${secretKey}`;
        const response = await fetch(url);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response body:", errorText);
          throw new Error(`Failed to fetch archetypes: ${response.status}`);
        }

        const data = await response.json();
        setArchetypes(data || []);
      } catch (error) {
        console.error("Error in fetchArchetypes:", error);
        addToast({
          title: "Error",
          description: "Failed to load archetypes",
          color: "danger",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchArchetypes();
  }, [gameId, isEnabled, isDev]);

  const handleSave = async () => {
    setSaving(true);

    try {
      // Default to localhost in development
      const apiUrl =
        process.env.NEXT_PUBLIC_API_URL ||
        (process.env.NODE_ENV === "development"
          ? "http://localhost:3001"
          : "https://api.tcgfan.com");
      const secretKey = process.env.NEXT_PUBLIC_BACKEND_SECRET_KEY;

      if (!secretKey) {
        addToast({
          title: "Error",
          description: "NEXT_PUBLIC_BACKEND_SECRET_KEY not configured",
          color: "danger",
        });
        return;
      }

      const url = `${apiUrl}/api/decklists/${decklistId}/archetype?API_KEY=${secretKey}`;
      const body = JSON.stringify({
        leader_id: selectedArchetype || null,
      });

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Save error response:", errorText);
        throw new Error(
          `Failed to update archetype: ${response.status} - ${errorText}`,
        );
      }

      await response.json();

      addToast({
        title: "Success",
        description:
          "Archetype updated! Hard refresh (Cmd+Shift+R or Ctrl+Shift+R) to see changes.",
        color: "success",
      });
    } catch (error) {
      console.error("Error updating archetype:", error);
      addToast({
        title: "Error",
        description: `Failed to update archetype: ${(error as Error).message}`,
        color: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <div className="border-2 border-warning rounded-lg p-4 bg-warning-50/20 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold text-warning-600 uppercase">
          Dev Tool
        </span>
      </div>
      <p className="text-sm text-default-600 mb-3">
        Set the archetype (leader_id) for this decklist
      </p>
      <div className="flex gap-2">
        <Select
          label="Archetype"
          placeholder={loading ? "Loading..." : "Select archetype"}
          selectionMode="single"
          selectedKeys={
            selectedArchetype ? new Set([selectedArchetype]) : new Set()
          }
          onSelectionChange={(keys) => {
            const keysArray = Array.from(keys);
            const selected = keysArray[0] as string;
            setSelectedArchetype(selected || "");
          }}
          isDisabled={loading || saving}
          className="flex-1"
          size="sm"
          renderValue={(items) => {
            if (!items || items.length === 0) {
              return <span className="text-default-400">Select archetype</span>;
            }
            const item = items[0];
            const archetype = archetypes.find((a) => a.name === item.key);
            if (!archetype) return <span>{item.key}</span>;

            const imageUrl = gameAssetUrl(
              "palworld",
              "archetype-squares",
              `${archetype.name}.webp`,
              "1.0.5",
            );
            return (
              <div className="flex items-center gap-2">
                <Image
                  src={imageUrl}
                  alt={archetype.display_name}
                  className="w-6 h-6 rounded"
                />
                <span>
                  {archetype.display_name} ({archetype.colors.join("/")})
                </span>
              </div>
            );
          }}
        >
          {[
            <SelectItem key="" textValue="None (Clear archetype)">
              None (Clear archetype)
            </SelectItem>,
            ...archetypes.map((archetype) => (
              <SelectItem
                key={archetype.name}
                textValue={`${archetype.display_name} (${archetype.colors.join("/")})`}
                startContent={
                  <Image
                    src={gameAssetUrl(
                      "palworld",
                      "archetype-squares",
                      `${archetype.name}.webp`,
                      "1.0.5",
                    )}
                    alt={archetype.display_name}
                    className="w-8 h-8 rounded"
                  />
                }
              >
                {archetype.display_name} ({archetype.colors.join("/")})
              </SelectItem>
            )),
          ]}
        </Select>
        <Button
          color="primary"
          size="sm"
          onPress={handleSave}
          isLoading={saving}
          isDisabled={loading}
        >
          Save
        </Button>
      </div>
      {currentArchetypeId && (
        <p className="text-xs text-default-400 mt-2">
          Current: {currentArchetypeId}
        </p>
      )}
    </div>
  );
}
